/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { getCurrentUser, submitForApproval } from 'blb-api/ron';
import * as Ron from 'blb-ui-types/ron';
import { Box, Text } from 'grommet';
import { NotaryStatus, NotaryStatusText, UserCredentialStatus } from 'blb-constants/ron';
import * as Sentry from '@sentry/browser';
import { fullName } from 'blb-utils/format';
import { Heading } from '@blend/alchemy';
import { NotarySubmitApproval } from 'blb-components/AuthenticatedApp/RON/Profile/NotarySubmitApproval';
import { styledAttrs } from 'blb-utils/misc';

const StatusStyle = {
  [NotaryStatus.ACTIVE]: {
    background: 'accentGreen',
    color: 'white',
  },
  [NotaryStatus.REJECTED]: {
    background: 'accentRed',
    color: 'white',
  },
  [NotaryStatus.TERMINATED]: {
    background: 'accentRed',
    color: 'white',
  },
  [NotaryStatus.EXPIRED]: {
    background: 'accentRed',
    color: 'white',
  },
  [NotaryStatus.UNDER_REVIEW]: {
    background: 'cloud70',
    color: 'night60',
  },
  [NotaryStatus.NEW]: {
    background: 'cloud70',
    color: 'night60',
  },
};

const ProfileHeaderWrapper = styledAttrs(Box, {
  align: 'center',
  direction: 'row',
  justify: 'between',
  pad: { bottom: 'large' },
});

const NotaryStatusTag: React.FC<{ status: NotaryStatus }> = ({ status }) => {
  const style = StatusStyle[status] || StatusStyle[UserCredentialStatus.NEW];
  return (
    <Box
      background={style.background}
      direction="column"
      justify="center"
      pad={{ horizontal: 'xsmall', vertical: '3px' }}
      round="3px"
    >
      <Text color={style.color} id="it-notaryProfileHeader-status" size="11px" weight="bold">
        {NotaryStatusText[status].toUpperCase()}
      </Text>
    </Box>
  );
};

export const NotaryProfileHeader: React.FC<{}> = () => {
  const [user, setUser] = useState<Ron.API.UserResponse>();

  const fetchUser = async () => {
    try {
      const user = await getCurrentUser();
      setUser(user);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchUser();
  }, []);

  const userName = fullName(user);

  const handleSubmitForApproval = async () => {
    await submitForApproval();
    await fetchUser();
  };
  return (
    <ProfileHeaderWrapper id="it-notaryProfileHeader">
      <Box direction="row" gap="small">
        <Heading.H200 id="it-notaryProfileHeader-name" screenReaderText={userName}>
          {userName}
        </Heading.H200>
        {user && <NotaryStatusTag status={user.status} />}
      </Box>

      <NotarySubmitApproval onSubmitForApproval={handleSubmitForApproval} user={user} />
    </ProfileHeaderWrapper>
  );
};
